<template>
    <div class="chat-quote-message">
        <div v-if="isRecall" class="recall">{{ msgInfo.content }}</div>
        <div v-if="!isRecall && showName" class="send-user">
            {{ showName + ":" }}
        </div>
        <div v-if="!isRecall" class="quote-content">
            <span v-if="msgInfo.type == $enums.MESSAGE_TYPE.TEXT" class="quote-text"
                v-html="$emo.transform($str.html2Escape(msgInfo.content), 'emoji-small')"></span>
            <div v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.IMAGE">
                <asset-image class="quote-image" :src="contentData.thumbUrl" :strict="true" @click.native.stop="showFullImageBox" />
            </div>
            <inline-video-player
                v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.VIDEO"
                class="quote-video-player"
                variant="history"
                :source="videoSource"
                :poster="videoPoster"
                :loading="videoPreparing"
                :load-error="videoLoadError"
                :previewable="canPreviewVideo"
                :duration="contentData.duration"
                :content-width="contentData.width"
                :content-height="contentData.height"
                :max-width="160"
                :max-height="120"
                :min-width="120"
                :fallback-aspect-ratio="videoAspectRatio"
                @click.native.stop
                @request-load="onPlayVideo"
                @request-preview="onPreviewVideo"
                @error="onVideoLoadError"
                @poster-error="onVideoPosterError"
            />
            <div v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.AUDIO">
                <audio class="quote-audio" controls :src="audioSource" @play="onPlayVoice" @error="onAudioLoadError"></audio>
            </div>
            <div class="quote-file" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.FILE">
                <div class="file-info">
                    <el-link class="file-name" :underline="true" type="primary" @click.native.prevent.stop="onPreviewFile">
                        {{ contentData.name }}
                    </el-link>
                    <div class="file-meta">
                        <div class="file-type-tag" :class="`file-type-tag--${fileTypeMeta.tone}`">{{ fileTypeMeta.fullLabel }}</div>
                        <div class="file-size">{{ fileSize }}</div>
                    </div>
                </div>
                <div class="file-icon" :class="`file-icon--${fileTypeMeta.tone}`">
                    <span class="file-icon__label">{{ fileTypeMeta.shortLabel }}</span>
                </div>
            </div>
            <div class="quote-card" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.USER_CARD">
                <div class="card-body">
                    <head-image :url="contentData.headImage" :name="contentData.nickName" radius="10%"
                        :size="32"></head-image>
                    <div class="name">
                        {{ contentData.nickName }}
                    </div>
                </div>
                <div class="card-tip">个人名片</div>
            </div>
            <div class="quote-card" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.GROUP_CARD">
                <div class="card-body">
					<head-image :url="contentData.headImage" :name="contentData.groupName" radius="10%" variant="group"
                        :size="32"></head-image>
                    <div class="name">
                        {{ contentData.groupName }}
                    </div>
                </div>
                <div class="card-tip">群名片</div>
            </div>
            <div v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.STICKER">
                <asset-image class="quote-image" :src="contentData.imageUrl" @click.native.stop="showFullSticker" />
            </div>
            <div v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.MERGE_FORWARD" class="quote-text"
                @click.stop="showMergeForward">
                [合并转发] 共{{ contentData.messages.length }}条消息
            </div>
            <div v-else class="quote-text">[暂不支持该消息类型]</div>
        </div>
        <chat-merge-forward ref="mergeForward"></chat-merge-forward>
    </div>
</template>

<script>
import HeadImage from '../common/HeadImage.vue';
import AssetImage from '../common/AssetImage.vue';
import InlineVideoPlayer from "./InlineVideoPlayer.vue";
import ChatMergeForward from "./ChatMergeForward.vue";
import { getFileExtension, resolveFileTypeMeta } from "../../utils/fileTypeMeta";
export default {
    name: "chatQuoteMessage",
    components: { HeadImage, AssetImage, InlineVideoPlayer, ChatMergeForward },
    props: {
        showName: {
            type: String,
            required: true
        },
        msgInfo: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            resolvedVideoUrl: '',
            resolvedVideoPoster: '',
            resolvedAudioUrl: '',
            videoPreparing: false,
            videoLoadError: false,
            mediaRequestId: 0
        }
    },
    methods: {
        showAssetError(message) {
            this.$message.error(message || '资源暂不可用');
        },
        hasAssetSource(source) {
            return this.$assetResolver.hasAssetSource(source);
        },
        async syncMediaAssets() {
            const requestId = ++this.mediaRequestId;
            this.resolvedVideoUrl = '';
            this.resolvedVideoPoster = '';
            this.resolvedAudioUrl = '';
            this.videoPreparing = false;
            this.videoLoadError = false;
            if (this.msgInfo.type == this.$enums.MESSAGE_TYPE.VIDEO) {
                this.resolvedVideoPoster = await this.resolveMediaUrl(this.contentData.coverUrl, requestId, {
                    kind: 'cover'
                });
            }
        },
        async resolveMediaUrl(source, requestId, extra = {}, notifyError = false) {
            if (!source) {
                return '';
            }
            if (!this.hasAssetSource(source)) {
                if (notifyError && requestId === this.mediaRequestId) {
                    this.showAssetError('资源暂不可用');
                }
                return '';
            }
            try {
                const url = await this.$assetResolver.resolveUrl(source, extra);
                if (requestId !== this.mediaRequestId) {
                    return '';
                }
                return url;
            } catch (error) {
                if (notifyError && requestId === this.mediaRequestId) {
                    this.showAssetError(error && error.message ? error.message : '资源加载失败');
                }
                return '';
            }
        },
        async onPlayVideo(event) {
            if (this.resolvedVideoUrl || this.videoPreparing) {
                return;
            }
            if (event && typeof event.preventDefault === 'function') {
                event.preventDefault();
            }
            const requestId = this.mediaRequestId;
            this.videoPreparing = true;
            this.videoLoadError = false;
            const videoUrl = await this.resolveMediaUrl(this.contentData.videoUrl, requestId, {
                kind: 'video'
            }, true);
            if (!videoUrl || requestId !== this.mediaRequestId) {
                if (requestId === this.mediaRequestId) {
                    this.videoPreparing = false;
                    this.videoLoadError = true;
                }
                return;
            }
            this.resolvedVideoUrl = videoUrl;
            if (!this.resolvedVideoPoster) {
                this.resolvedVideoPoster = await this.resolveMediaUrl(this.contentData.coverUrl, requestId, {
                    kind: 'cover'
                });
            }
            if (requestId === this.mediaRequestId) {
                this.videoPreparing = false;
                this.videoLoadError = false;
            }
        },
        onVideoLoadError() {
            if (!this.resolvedVideoUrl) {
                return;
            }
            this.resolvedVideoUrl = '';
            this.videoPreparing = false;
            this.videoLoadError = true;
            this.showAssetError('资源加载失败，请重试');
        },
        onVideoPosterError() {
            this.resolvedVideoPoster = '';
        },
        async onPlayVoice() {
            if (this.resolvedAudioUrl) {
                return;
            }
            const requestId = this.mediaRequestId;
            const audioUrl = this.resolvedAudioUrl || await this.resolveMediaUrl(this.contentData.url, requestId, {
                kind: 'audio'
            }, true);
            if (!audioUrl || requestId !== this.mediaRequestId) {
                return;
            }
            this.resolvedAudioUrl = audioUrl;
            this.$nextTick(() => {
                const player = this.$el ? this.$el.querySelector('audio.quote-audio') : null;
                if (player) {
                    player.play().catch(() => {});
                }
            });
        },
        onAudioLoadError() {
            if (!this.resolvedAudioUrl) {
                return;
            }
            this.resolvedAudioUrl = '';
            this.showAssetError('资源加载失败，请重试');
        },
        showFullImageBox() {
            let imageUrl = this.imagePreviewSource;
            if (imageUrl) {
                this.$eventBus.$emit("openFullImage", {
                    url: imageUrl,
                    strict: true,
                    kind: 'image',
                    fileName: this.imagePreviewMeta.fileName,
                    contentType: this.imagePreviewMeta.contentType
                });
            }
        },
        showFullSticker() {
            const imageUrl = this.contentData.imageUrl;
            if (imageUrl) {
                this.$eventBus.$emit("openFullImage", imageUrl);
            }
        },
        onPreviewVideo() {
            if (!this.contentData.videoUrl) {
                this.showAssetError('视频暂不可用');
                return;
            }
            this.$eventBus.$emit("openFullVideo", {
                url: this.contentData.videoUrl,
                poster: this.contentData.coverUrl || '',
                strict: true
            });
        },
        showMergeForward() {
            this.$refs.mergeForward.open(this.contentData);
        },
        onPreviewFile() {
            if (!this.contentData.url) {
                this.showAssetError('文件暂不可用');
                return;
            }
            this.$eventBus.$emit("openPreviewFile", {
                url: this.contentData.url,
                fileName: this.contentData.name,
                strict: true
            });
        }
    },
    computed: {
        quoteMessageText() {
            return this.msgInfo.content + "hello";
        },
        contentData() {
            return JSON.parse(this.msgInfo.content)
        },
        imagePreviewSource() {
            return this.contentData.originUrl || this.contentData.thumbUrl || '';
        },
        imagePreviewMeta() {
            return {
                fileName: this.contentData.name || this.contentData.fileName || 'image',
                contentType: this.contentData.contentType || this.contentData.mimeType || 'image/*'
            };
        },
        videoSource() {
            return this.resolvedVideoUrl || undefined;
        },
        videoPoster() {
            return this.resolvedVideoPoster || undefined;
        },
        videoAspectRatio() {
            const width = Number(this.contentData.width) || 0;
            const height = Number(this.contentData.height) || 0;
            if (!width || !height) {
                return '16 / 9';
            }
            return `${Math.max(1, width)} / ${Math.max(1, height)}`;
        },
        canPreviewVideo() {
            return this.msgInfo.type == this.$enums.MESSAGE_TYPE.VIDEO && Boolean(this.contentData.videoUrl);
        },
        audioSource() {
            return this.resolvedAudioUrl || undefined;
        },
        fileSize() {
            let size = this.contentData.size;
            if (size > 1024 * 1024) {
                return Math.round(size / 1024 / 1024) + "M";
            }
            if (size > 1024) {
                return Math.round(size / 1024) + "KB";
            }
            return size + "B";
        },
        fileTypeMeta() {
            return resolveFileTypeMeta(getFileExtension(this.contentData.name));
        },
        isRecall() {
            return this.msgInfo.status == this.$enums.MESSAGE_STATUS.RECALL
        }
    },
    watch: {
        'msgInfo.content': {
            immediate: true,
            handler() {
                this.syncMediaAssets();
            }
        }
    },
    beforeDestroy() {
        this.mediaRequestId += 1;
    }
}
</script>

<style lang="scss" scoped>
.chat-quote-message {
    background: var(--im-control-soft-bg);
    padding: 8px;
    display: inline-flex;
    align-items: flex-start;
    gap: 8px;
    border-radius: 6px;
    font-size: var(--im-font-size-smaller);
    color: var(--im-text-color-light);
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;

    .send-user {
        flex-shrink: 0;
        font-weight: 600;
        white-space: nowrap;
    }

    .quote-content {
        max-width: 100%;
        min-width: 0;

        .quote-text {
            display: inline-block;
            max-width: 100%;
            text-align: left;
            white-space: pre-wrap;
            word-break: break-all;
        }

        .quote-image {
            display: block;
            max-width: min(100%, 80px);
            max-height: 60px;
            width: auto;
            height: auto;
            cursor: pointer;
        }

        .quote-video-player {
            display: block;
            max-width: min(100%, 160px);
        }

        .quote-audio {
            display: block;
            width: min(100%, 220px);
            max-width: 100%;
            height: 38px;
        }


        .quote-file {
            display: flex;
            flex-wrap: nowrap;
            flex-direction: row;
            align-items: center;
            cursor: pointer;
            padding: 6px 10px;
            background: var(--im-control-bg);
            border: 1px solid var(--im-control-border);
            border-radius: 6px;
            width: min(100%, 240px);
            max-width: 100%;
            min-width: 0;
            box-sizing: border-box;

            .file-info {
                flex: 1;
                min-width: 0;
                height: 100%;
                text-align: left;
                font-size: 14px;
                margin-right: 10px;

                .file-name {
                    display: -webkit-box;
                    width: 100%;
                    max-width: 100%;
                    font-size: 14px;
                    margin-bottom: 4px;
                    overflow: hidden;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 2;
                    word-break: break-all;
                }

                .file-meta {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    flex-wrap: wrap;
                }

                .file-type-tag,
                .file-size {
                    display: inline-flex;
                    align-items: center;
                    height: 18px;
                    padding: 0 6px;
                    border-radius: 999px;
                    font-size: var(--im-font-size-smaller);
                    font-weight: 500;
                }

                .file-size {
                    background: rgba(var(--im-black-rgb), 0.04);
                    color: var(--im-text-color-light);
                }

                .file-type-tag {
                    background: rgba(var(--im-black-rgb), 0.06);
                    color: var(--im-text-color-secondary);
                }

                .file-type-tag--primary {
                    background: rgba(var(--im-primary-rgb), 0.1);
                    color: var(--im-color-primary);
                }

                .file-type-tag--success {
                    background: rgba(var(--im-success-rgb, 103, 194, 58), 0.12);
                    color: var(--im-color-success, #67c23a);
                }

                .file-type-tag--warning {
                    background: rgba(var(--im-warning-rgb, 230, 162, 60), 0.12);
                    color: var(--im-color-warning, #e6a23c);
                }

                .file-type-tag--danger {
                    background: rgba(var(--im-danger-rgb), 0.1);
                    color: var(--im-color-danger);
                }

                .file-type-tag--teal {
                    background: rgba(20, 184, 166, 0.12);
                    color: #0f766e;
                }
            }

            .file-icon {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 34px;
                height: 34px;
                border-radius: 10px;
                background: rgba(var(--im-black-rgb), 0.05);
                border: 1px solid rgba(var(--im-black-rgb), 0.06);

                .file-icon__label {
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 0.4px;
                    color: var(--im-text-color-secondary);
                }
            }

            .file-icon--primary {
                background: rgba(var(--im-primary-rgb), 0.08);
                border-color: rgba(var(--im-primary-rgb), 0.12);
            }

            .file-icon--primary .file-icon__label {
                color: var(--im-color-primary);
            }

            .file-icon--success {
                background: rgba(var(--im-success-rgb, 103, 194, 58), 0.1);
                border-color: rgba(var(--im-success-rgb, 103, 194, 58), 0.14);
            }

            .file-icon--success .file-icon__label {
                color: var(--im-color-success, #67c23a);
            }

            .file-icon--warning {
                background: rgba(var(--im-warning-rgb, 230, 162, 60), 0.1);
                border-color: rgba(var(--im-warning-rgb, 230, 162, 60), 0.14);
            }

            .file-icon--warning .file-icon__label {
                color: var(--im-color-warning, #e6a23c);
            }

            .file-icon--danger {
                background: rgba(var(--im-danger-rgb), 0.08);
                border-color: rgba(var(--im-danger-rgb), 0.12);
            }

            .file-icon--danger .file-icon__label {
                color: var(--im-color-danger);
            }

            .file-icon--teal {
                background: rgba(20, 184, 166, 0.1);
                border-color: rgba(20, 184, 166, 0.14);
            }

            .file-icon--teal .file-icon__label {
                color: #0f766e;
            }
        }
    }

    .quote-card {
        display: flex;
        flex-direction: column;
        flex-wrap: nowrap;
        border: 1px solid var(--im-control-border);
        border-radius: 6px;
        padding: 6px 10px;
        height: 60px;
        width: min(100%, 150px);
        max-width: 100%;
        min-width: 0;
        cursor: pointer;
        background: var(--im-control-bg);
        box-sizing: border-box;

        .card-body {
            flex: 1;
            display: flex;
            align-items: center;
            border-bottom: 1px solid rgba(var(--im-black-rgb), 0.06);

            .name {
                min-width: 0;
                margin-left: 8px;
                font-weight: 600;
                font-size: var(--im-font-size-smaller);
                color: var(--im-text-color);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }

        .card-tip {
            margin-top: 4px;
            color: var(--im-text-color-light);
            font-size: 10px;
            text-align: left;
        }
    }
}
</style>
